import {
  throttle,
  minWidthQuery,
  getObserver,
  layoutRecalcManager,
  layoutPhaseMeasure,
  layoutPhaseApply,
} from '@theme/theme-global';

class MobileDocker extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  get section() {
    return (this._section = this._section || this.closest('.mobile-docker-section'));
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.setAttribute('loaded', '');

    this.currentScrollTop = window.scrollY;
    this.scrollingDown = false;
    this._lastHeight = null;
    this._lastDynamicHeight = null;
    this._pendingHeight = null;
    this._vvWidth = window.innerWidth;
    this._vvHeight = window.innerHeight;

    this._onLayoutRecalc = this._onLayoutRecalc.bind(this);
    this._onScroll = throttle(this.onScroll.bind(this));
    this._onMqChange = this._onMqChange.bind(this);
    this._onViewportResize = throttle(this.onViewportResize.bind(this), 100);
    this._onSectionSelect = () => this.section.classList.add('shopify-active');
    this._onSectionDeselect = () => this.section.classList.remove('shopify-active');
    this._onHeaderSectionChange = this.onHeaderSectionChange.bind(this);

    layoutRecalcManager.register(this._onLayoutRecalc);

    window.addEventListener('scroll', this._onScroll, { passive: true });
    minWidthQuery(1024).addEventListener('change', this._onMqChange);
    window.visualViewport?.addEventListener('resize', this._onViewportResize);
    document.addEventListener('shopify:section:load', this._onHeaderSectionChange);
    document.addEventListener('shopify:section:unload', this._onHeaderSectionChange);
    document.addEventListener('shopify:section:reorder', this._onHeaderSectionChange);

    if (Shopify.designMode) {
      this.section.addEventListener('shopify:section:select', this._onSectionSelect);
      this.section.addEventListener('shopify:section:deselect', this._onSectionDeselect);
    }

    this.bindHeader();
    this.syncActive();
    this._scheduleIdleHeight();
  }

  destroy() {
    this._cancelIdleHeight();
    layoutRecalcManager.unregister(this._onLayoutRecalc);
    window.removeEventListener('scroll', this._onScroll);
    minWidthQuery(1024).removeEventListener('change', this._onMqChange);
    window.visualViewport?.removeEventListener('resize', this._onViewportResize);
    document.removeEventListener('shopify:section:load', this._onHeaderSectionChange);
    document.removeEventListener('shopify:section:unload', this._onHeaderSectionChange);
    document.removeEventListener('shopify:section:reorder', this._onHeaderSectionChange);
    this.unbindHeader();

    if (Shopify.designMode && this.section) {
      this.section.removeEventListener('shopify:section:select', this._onSectionSelect);
      this.section.removeEventListener('shopify:section:deselect', this._onSectionDeselect);
    }

    document.documentElement.style.setProperty('--mobileDockerHeight', '0px');
    document.documentElement.style.setProperty('--mobileDockerHeightDynamic', '0px');
    this._lastHeight = null;
    this._lastDynamicHeight = null;
    this._pendingHeight = null;
    this.initialized = false;
  }

  _isDesktop() {
    return minWidthQuery(1024).matches;
  }

  _scheduleHeight() {
    if (this._isDesktop()) return;
    layoutRecalcManager.request(this._onLayoutRecalc);
  }

  _scheduleIdleHeight() {
    this._cancelIdleHeight();

    if (this._isDesktop()) return;

    const run = () => {
      this._idleHeightId = null;
      this._idleHeightTimer = null;
      this._scheduleHeight();
    };

    if (window.Shopify?.designMode) {
      run();
      return;
    }

    if ('requestIdleCallback' in window) {
      this._idleHeightId = requestIdleCallback(run, { timeout: 2500 });
      return;
    }

    this._idleHeightTimer = setTimeout(run, 0);
  }

  _cancelIdleHeight() {
    if (this._idleHeightId != null && 'cancelIdleCallback' in window) {
      cancelIdleCallback(this._idleHeightId);
    }
    if (this._idleHeightTimer != null) {
      clearTimeout(this._idleHeightTimer);
    }
    this._idleHeightId = null;
    this._idleHeightTimer = null;
  }

  _onMqChange() {
    if (this._isDesktop()) {
      this._cancelIdleHeight();
      this._applyHeight('0px');
      this.setDynamicHeight(false, '0px');
      return;
    }

    this._scheduleHeight();
  }

  _onLayoutRecalc(phase) {
    if (this._isDesktop()) {
      if (layoutPhaseApply(phase)) {
        this._applyHeight('0px');
        this.setDynamicHeight(false, '0px');
      }
      return;
    }

    if (layoutPhaseMeasure(phase)) {
      this._pendingHeight = `${this.getBoundingClientRect().height.toFixed(1)}px`;
    }

    if (layoutPhaseApply(phase)) {
      this._applyHeight(this._pendingHeight);
      this.setDynamicHeight(this.classList.contains('active'), this._pendingHeight);
    }
  }

  _applyHeight(height) {
    if (!height || height === this._lastHeight) return;
    this._lastHeight = height;
    document.documentElement.style.setProperty('--mobileDockerHeight', height);
  }

  onHeaderSectionChange(event) {
    if (!event.target?.classList?.contains('header-main')) return;

    requestAnimationFrame(() => {
      this.bindHeader();
      this.syncActive();
      this._scheduleHeight();
    });
  }

  unbindHeader() {
    if (this._headerObserver && this.header) {
      this._headerObserver.unobserve(this.header);
    }
    this._headerObserver = null;
    this.header = null;
  }

  bindHeader() {
    this.unbindHeader();

    this.header = document.querySelector('.header-main');
    this.isStickyNone = !this.header || !this.header.classList.contains('header-sticky');
    this.headerOutOfView = !this.header;

    if (this.isStickyNone && this.header) {
      this.observeHeader();
    }
  }

  observeHeader() {
    this._headerObserver = getObserver({ threshold: 0 }, false, true);
    this._headerObserver.observe(this.header, (entry) => {
      this.headerOutOfView = !entry.isIntersecting;
      this.syncActive();
    });
  }

  onViewportResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const widthChanged = Math.abs(width - this._vvWidth) > 1;
    const heightDelta = Math.abs(height - this._vvHeight);

    this._vvWidth = width;
    this._vvHeight = height;

    if (!widthChanged && heightDelta < 120) return;

    this._scheduleHeight();
  }

  onScroll() {
    if (this._isDesktop()) return;

    const scrollTop = window.scrollY;
    const delta = scrollTop - this.currentScrollTop;

    if (Math.abs(delta) < 10) return;

    this.scrollingDown = delta > 0;
    this.currentScrollTop = scrollTop;
    this.syncActive();
  }

  syncActive() {
    let isActive = false;

    if (!this._isDesktop()) {
      if (this.isStickyNone) {
        isActive = this.headerOutOfView;
      } else {
        isActive = this.scrollingDown;
      }
    }

    this.classList.toggle('active', isActive);

    if (isActive && !this._lastHeight) {
      this._scheduleHeight();
      return;
    }

    this.setDynamicHeight(isActive);
  }

  setDynamicHeight(isActive = this.classList.contains('active'), height = null) {
    const size = height || this._lastHeight;
    const dynamicHeight = isActive && size ? size : '0px';

    if (this._lastDynamicHeight === dynamicHeight) return;
    this._lastDynamicHeight = dynamicHeight;
    document.documentElement.style.setProperty('--mobileDockerHeightDynamic', dynamicHeight);
  }
}

if (!customElements.get('mobile-docker')) {
  customElements.define('mobile-docker', MobileDocker);
}
